import { Fragment } from 'react'

import {
  Checkbox,
  Collapse,
  Radio,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import { useForm } from '@mantine/form'

import Button from 'src/components/Buttons/Button/Button'
import { VoEProvider } from 'src/graphql/types/carriers'
import IconEmailSimple from 'src/icons/IconEmailSimple'
import { VOE_PROVIDER_OPTIONS } from 'src/lib/voeProvider.utils'

import OptionCard from './OptionCard'

export type OptOutScope = 'CARRIER' | 'GLOBAL'

export type OptOutReason =
  | 'PLATFORM'
  | 'WRONG_CONTACT'
  | 'POLICY'
  | 'NOT_EMPLOYED'
  | 'OTHER'
  | ''

export interface OptOutFormValues {
  scope: OptOutScope
  reason: OptOutReason
  reasonDetail: string
  platform: string
  platformOther: string
  contactName: string
  contactEmail: string
  contactPhone: string
  contactPhoneIsFax: boolean
}

export const OPT_OUT_REASONS: { value: OptOutReason; label: string }[] = [
  {
    value: 'PLATFORM',
    label: 'We only handle verifications through a platform',
  },
  { value: 'WRONG_CONTACT', label: 'I am not the right contact here' },
  { value: 'POLICY', label: 'We do not respond to employment verifications' },
  { value: 'NOT_EMPLOYED', label: 'This person never worked here' },
  { value: 'OTHER', label: 'Something else' },
]

const inputClassNames = {
  label: 'text-sm font-medium text-doubleNickel-gray-700 pb-1.5',
  input: 'text-sm',
  description: 'text-xs leading-5 text-doubleNickel-gray-500 pt-1.5',
}

interface VoEOptOutFormProps {
  carrierName: string
  dotNumber: string | null
  maskedEmail: string
  carrierAlreadySuppressed?: boolean
  canScopeToCarrier?: boolean
  onSubmit: (values: OptOutFormValues) => void
  submitting?: boolean
}

const VoEOptOutForm = ({
  carrierName,
  dotNumber,
  maskedEmail,
  carrierAlreadySuppressed = false,
  canScopeToCarrier = true,
  onSubmit,
  submitting = false,
}: VoEOptOutFormProps) => {
  const form = useForm<OptOutFormValues>({
    initialValues: {
      scope:
        carrierAlreadySuppressed || !canScopeToCarrier ? 'GLOBAL' : 'CARRIER',
      reason: '',
      reasonDetail: '',
      platform: '',
      platformOther: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      contactPhoneIsFax: false,
    },
  })

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="xs" className="mb-7">
        <div className="flex items-center gap-2">
          <IconEmailSimple className="h-4 w-4 fill-none stroke-doubleNickel-brand-500" />
          <Text className="text-xs font-semibold uppercase tracking-wider text-doubleNickel-gray-500">
            Employment verification
          </Text>
        </div>
        <Text className="text-xl font-semibold leading-8 text-doubleNickel-gray-900">
          Stop receiving these requests
        </Text>
        <Text className="text-sm leading-relaxed text-doubleNickel-gray-600">
          We send employment verification requests to{' '}
          <span className="font-medium text-doubleNickel-gray-700">
            {maskedEmail}
          </span>{' '}
          for{' '}
          <span className="font-medium text-doubleNickel-gray-700">
            {carrierName}
          </span>
          {dotNumber ? ` · DOT ${dotNumber}` : ''}.
        </Text>
      </Stack>

      <Radio.Group
        value={form.values.scope}
        onChange={(value) => form.setFieldValue('scope', value as OptOutScope)}
        label="What should we stop?"
        classNames={{
          label: 'text-sm font-medium text-doubleNickel-gray-700 pb-2',
          root: 'mb-7',
        }}
      >
        <Stack gap="xs">
          {canScopeToCarrier && (
            <OptionCard
              value="CARRIER"
              label={`Requests for ${carrierName}`}
              description={
                carrierAlreadySuppressed
                  ? 'Already stopped — you asked us this before.'
                  : 'We may still contact you about a different employer.'
              }
              selected={form.values.scope === 'CARRIER'}
              onSelect={(value) =>
                form.setFieldValue('scope', value as OptOutScope)
              }
            />
          )}
          <OptionCard
            value="GLOBAL"
            label="All employment verification requests"
            description="This address will never be contacted again, for any employer."
            selected={form.values.scope === 'GLOBAL'}
            onSelect={(value) =>
              form.setFieldValue('scope', value as OptOutScope)
            }
          />
        </Stack>
      </Radio.Group>

      <Radio.Group
        value={form.values.reason}
        onChange={(value) =>
          form.setFieldValue('reason', value as OptOutReason)
        }
        label="Why? (optional)"
        classNames={{
          label: 'text-sm font-medium text-doubleNickel-gray-700 pb-2',
          root: 'mb-3',
        }}
      >
        <Stack gap="xs">
          {OPT_OUT_REASONS.map((reason) => (
            <Fragment key={reason.value}>
              <OptionCard
                value={reason.value}
                label={reason.label}
                selected={form.values.reason === reason.value}
                onSelect={(value) =>
                  form.setFieldValue('reason', value as OptOutReason)
                }
              />
              {reason.value === 'PLATFORM' && (
                <Collapse
                  in={form.values.reason === 'PLATFORM'}
                  transitionDuration={150}
                >
                  <Stack gap="xs" className="pb-1">
                    <Select
                      aria-label="Which platform"
                      placeholder="Choose a platform"
                      data={VOE_PROVIDER_OPTIONS}
                      classNames={inputClassNames}
                      {...form.getInputProps('platform')}
                    />
                    <Collapse
                      in={form.values.platform === VoEProvider.OTHER}
                      transitionDuration={150}
                    >
                      <TextInput
                        aria-label="Platform name"
                        placeholder="What is it called?"
                        classNames={inputClassNames}
                        {...form.getInputProps('platformOther')}
                      />
                    </Collapse>
                  </Stack>
                </Collapse>
              )}
            </Fragment>
          ))}
        </Stack>
      </Radio.Group>

      <Textarea
        aria-label="Anything else we should know"
        placeholder="Anything else we should know?"
        autosize
        minRows={2}
        maxRows={6}
        classNames={inputClassNames}
        className="mb-7"
        {...form.getInputProps('reasonDetail')}
      />

      <Stack gap="xs" className="mb-7">
        <div>
          <Text className="text-sm font-medium text-doubleNickel-gray-700">
            Who should we contact instead? (optional)
          </Text>
          <Text className="text-xs leading-5 text-doubleNickel-gray-500">
            Drivers who worked at this carrier need their employment confirmed
            to be hired.
          </Text>
        </div>
        <TextInput
          aria-label="Contact name"
          placeholder="Name or department"
          classNames={inputClassNames}
          {...form.getInputProps('contactName')}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <TextInput
            aria-label="Contact email"
            placeholder="Email"
            type="email"
            classNames={inputClassNames}
            className="flex-1"
            {...form.getInputProps('contactEmail')}
          />
          <TextInput
            aria-label="Contact phone"
            placeholder="Phone"
            type="tel"
            classNames={inputClassNames}
            className="flex-1"
            {...form.getInputProps('contactPhone')}
          />
        </div>
        <Checkbox
          label="That number is a fax"
          classNames={{ label: 'text-sm text-doubleNickel-gray-600' }}
          {...form.getInputProps('contactPhoneIsFax', { type: 'checkbox' })}
        />
      </Stack>

      <Button
        type="submit"
        text="Stop sending requests"
        loading={submitting}
        className="h-12 w-full"
      />

      <Text className="pt-4 text-center text-xs leading-5 text-doubleNickel-gray-500">
        Takes effect immediately. Requests already in flight may still arrive.
      </Text>
    </form>
  )
}

export default VoEOptOutForm
